/**
 * STORAGE SERVICE
 * 
 * Handles storage usage calculation and tracking for users.
 * 
 * How it works:
 * 1. Calculate storage from user's items in Firestore (photo URLs → file sizes)
 * 2. Cache the result in the user document to avoid recalculating every time
 * 3. Update cache when photos are added/removed
 * 
 * Storage paths in Firebase Storage:
 * - users/{userId}/items/{itemId}/{filename} - Item photos
 * - users/{userId}/profile/{filename} - Avatar/banner images
 * - users/{userId}/creations/{filename} - Nail art creations
 * 
 * Note: We track "storageUsed" in the user document for quick access,
 * and periodically verify it matches actual storage.
 */

import { doc, getDoc, updateDoc, collection, getDocs } from 'firebase/firestore';
import { ref, getMetadata, listAll } from 'firebase/storage';
import { db, storage } from '../firebase/config';
import {
  getTier,
  isOverStorageLimit,
} from '../constants/storageTiers';
import type { StorageTierId, StorageTier } from '../constants/storageTiers';

// User's storage info stored in Firestore
export interface UserStorageInfo {
  tierId: StorageTierId;
  storageUsedBytes: number;
  storageLastCalculated: Date;
  subscriptionStatus?: 'active' | 'canceled' | 'past_due' | 'trialing';
  subscriptionEndDate?: Date;
  stripeCustomerId?: string;
  stripeSubscriptionId?: string;
  isBetaTester?: boolean; // Users with betaTermsAccepted have unlimited storage
}

// Result of checking if upload is allowed
export interface UploadCheckResult {
  allowed: boolean;
  reason?: string;
  currentUsageBytes: number;
  limitBytes: number;
  percentUsed: number;
  tier: StorageTier;
  suggestedTier?: StorageTier;
}

/**
 * Get the user's current storage tier info from Firestore
 */
export async function getUserStorageInfo(userId: string): Promise<UserStorageInfo> {
  try {
    const userDoc = await getDoc(doc(db, 'users', userId));
    
    if (!userDoc.exists()) {
      // New user - return defaults
      return {
        tierId: 'free',
        storageUsedBytes: 0,
        storageLastCalculated: new Date(),
      };
    }
    
    const data = userDoc.data();
    
    return {
      tierId: (data.subscription?.tierId || data.subscription?.tier || data.tierId || 'free') as StorageTierId,
      storageUsedBytes: data.storageUsedBytes || 0,
      storageLastCalculated: data.storageLastCalculated?.toDate() || new Date(),
      subscriptionStatus: data.subscription?.status,
      subscriptionEndDate: data.subscription?.endDate?.toDate(),
      stripeCustomerId: data.subscription?.stripeCustomerId,
      stripeSubscriptionId: data.subscription?.stripeSubscriptionId,
      // Check for betaTermsAccepted field to identify beta testers
      isBetaTester: data.betaTermsAccepted === true,
    };
  } catch (error) {
    console.error('Error getting user storage info:', error);
    return {
      tierId: 'free',
      storageUsedBytes: 0,
      storageLastCalculated: new Date(),
    };
  }
}

/**
 * Update the cached storage usage in Firestore
 */
export async function updateStorageUsage(userId: string, usedBytes: number): Promise<void> {
  try {
    const userRef = doc(db, 'users', userId);
    await updateDoc(userRef, {
      storageUsedBytes: usedBytes,
      storageLastCalculated: new Date(),
    });
  } catch (error) {
    console.error('Error updating storage usage:', error);
    throw error;
  }
}

/**
 * Add to the storage usage (call after successful upload)
 */
export async function addToStorageUsage(userId: string, bytesToAdd: number): Promise<void> {
  try {
    const currentInfo = await getUserStorageInfo(userId);
    const newUsage = currentInfo.storageUsedBytes + bytesToAdd;
    await updateStorageUsage(userId, newUsage);
  } catch (error) {
    console.error('Error adding to storage usage:', error);
    throw error;
  }
}

/**
 * Subtract from storage usage (call after photo deletion)
 */
export async function subtractFromStorageUsage(userId: string, bytesToSubtract: number): Promise<void> {
  try {
    const currentInfo = await getUserStorageInfo(userId);
    const newUsage = Math.max(0, currentInfo.storageUsedBytes - bytesToSubtract);
    await updateStorageUsage(userId, newUsage);
  } catch (error) {
    console.error('Error subtracting from storage usage:', error);
    throw error;
  }
}

/**
 * Calculate actual storage usage by listing all files in user's storage folder
 * This is more accurate but slower - use for periodic verification
 */
export async function calculateActualStorageUsage(userId: string): Promise<number> {
  try {
    const userStorageRef = ref(storage, `users/${userId}`);
    let totalBytes = 0;
    
    // Recursively list all files
    const listResult = await listAll(userStorageRef);
    
    // Get size of files in this directory
    for (const itemRef of listResult.items) {
      try {
        const metadata = await getMetadata(itemRef);
        totalBytes += metadata.size;
      } catch (e) {
        // File might have been deleted, skip it
        console.warn(`Could not get metadata for ${itemRef.fullPath}`);
      }
    }
    
    // Recursively process subdirectories
    for (const prefixRef of listResult.prefixes) {
      totalBytes += await calculateStorageInFolder(prefixRef.fullPath);
    }
    
    return totalBytes;
  } catch (error) {
    console.error('Error calculating storage usage:', error);
    return 0;
  }
}

/**
 * Helper to calculate storage in a specific folder
 */
async function calculateStorageInFolder(folderPath: string): Promise<number> {
  try {
    const folderRef = ref(storage, folderPath);
    let totalBytes = 0;
    
    const listResult = await listAll(folderRef);
    
    for (const itemRef of listResult.items) {
      try {
        const metadata = await getMetadata(itemRef);
        totalBytes += metadata.size;
      } catch (e) {
        // Skip files we can't access
      }
    }
    
    for (const prefixRef of listResult.prefixes) {
      totalBytes += await calculateStorageInFolder(prefixRef.fullPath);
    }
    
    return totalBytes;
  } catch (error) {
    return 0;
  }
}

/**
 * Quick estimate of storage from Firestore item photos
 * This is faster than listing Storage files but may be less accurate
 */
export async function estimateStorageFromItems(userId: string): Promise<number> {
  try {
    const itemsRef = collection(db, 'users', userId, 'items');
    const itemsSnapshot = await getDocs(itemsRef);
    
    let photoCount = 0;
    
    itemsSnapshot.forEach(doc => {
      const data = doc.data();
      if (data.photos && Array.isArray(data.photos)) {
        photoCount += data.photos.length;
      }
      if (data.swatchPhotos && Array.isArray(data.swatchPhotos)) {
        photoCount += data.swatchPhotos.length;
      }
    });
    
    // Estimate average compressed photo size (~150KB after compression)
    const AVERAGE_PHOTO_SIZE = 150 * 1024; // 150 KB
    
    return photoCount * AVERAGE_PHOTO_SIZE;
  } catch (error) {
    console.error('Error estimating storage:', error);
    return 0;
  }
}

/**
 * Check if user can upload a file of given size
 */
export async function checkCanUpload(
  userId: string, 
  fileSizeBytes: number
): Promise<UploadCheckResult> {
  const storageInfo = await getUserStorageInfo(userId);
  const tier = getTier(storageInfo.tierId);
  
  const currentUsage = storageInfo.storageUsedBytes;
  const projectedUsage = currentUsage + fileSizeBytes;
  const percentUsed = Math.round((currentUsage / tier.storageLimitBytes) * 100);
  
  // Beta testers have unlimited storage - always allow uploads
  if (storageInfo.isBetaTester) {
    return {
      allowed: true,
      currentUsageBytes: currentUsage,
      limitBytes: tier.storageLimitBytes,
      percentUsed,
      tier,
    };
  }
  
  // Check if they're already over limit
  if (isOverStorageLimit(currentUsage, tier)) {
    const nextTier = getNextUpgradeTier(tier);
    return {
      allowed: false,
      reason: 'You have reached your storage limit. Please upgrade or delete some photos to continue.',
      currentUsageBytes: currentUsage,
      limitBytes: tier.storageLimitBytes,
      percentUsed: Math.min(100, percentUsed),
      tier,
      suggestedTier: nextTier || undefined,
    };
  }
  
  // Check if this upload would put them over
  if (projectedUsage > tier.storageLimitBytes) {
    const nextTier = getNextUpgradeTier(tier);
    return {
      allowed: false,
      reason: 'This upload would exceed your storage limit. Please upgrade or delete some photos.',
      currentUsageBytes: currentUsage,
      limitBytes: tier.storageLimitBytes,
      percentUsed,
      tier,
      suggestedTier: nextTier || undefined,
    };
  }
  
  // Upload is allowed
  return {
    allowed: true,
    currentUsageBytes: currentUsage,
    limitBytes: tier.storageLimitBytes,
    percentUsed,
    tier,
  };
}

/**
 * Get the next tier that would give user more storage
 */
function getNextUpgradeTier(currentTier: StorageTier): StorageTier | null {
  const tierOrder: StorageTierId[] = ['free', 'casual', 'enthusiast', 'pro', 'museum'];
  const currentIndex = tierOrder.indexOf(currentTier.id);
  
  if (currentIndex === -1 || currentIndex >= tierOrder.length - 1) {
    return null;
  }
  
  return getTier(tierOrder[currentIndex + 1]);
}

/**
 * Update user's subscription tier
 */
export async function updateUserTier(
  userId: string, 
  tierId: StorageTierId,
  subscriptionData?: {
    status: 'active' | 'canceled' | 'past_due' | 'trialing';
    stripeCustomerId?: string;
    stripeSubscriptionId?: string;
    endDate?: Date;
  }
): Promise<void> {
  try {
    const userRef = doc(db, 'users', userId);
    
    const updateData: any = {
      'subscription.tierId': tierId,
    };
    
    if (subscriptionData) {
      if (subscriptionData.status) {
        updateData['subscription.status'] = subscriptionData.status;
      }
      if (subscriptionData.stripeCustomerId) {
        updateData['subscription.stripeCustomerId'] = subscriptionData.stripeCustomerId;
      }
      if (subscriptionData.stripeSubscriptionId) {
        updateData['subscription.stripeSubscriptionId'] = subscriptionData.stripeSubscriptionId;
      }
      if (subscriptionData.endDate) {
        updateData['subscription.endDate'] = subscriptionData.endDate;
      }
    }
    
    await updateDoc(userRef, updateData);
  } catch (error) {
    console.error('Error updating user tier:', error);
    throw error;
  }
}

/**
 * Recalculate and sync storage usage
 * Call this periodically or when there might be discrepancies
 */
export async function syncStorageUsage(userId: string): Promise<number> {
  const actualUsage = await calculateActualStorageUsage(userId);
  await updateStorageUsage(userId, actualUsage);
  return actualUsage;
}
